import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import React, {useCallback, useContext, useEffect, useMemo} from "react";
import {format, parseISO} from "date-fns";
import { ptBR } from "date-fns/locale";
import {FaRoute} from "react-icons/fa";
import {resolveGridColor} from "../utils/georeferenced-search-utils.js";
import {GeoreferencedSearchContext} from "../context/georeferenced-search-context.jsx";
import './geoferenced-search-table.css'

const API_URL = import.meta.env.VITE_API_URL;

const GeoreferencedSearchTable = ({rowData, className, markersData, replicatedPlates}) => {
   const {colorRegistry, setRegistryVersion, options, onGridReady, gridApi, setOrderedData, syncOrderedData, setHiddenDetails, centerOnSelectionRef,
       setCurrentPassage, setActivePlates, selection, setSelection} = useContext(GeoreferencedSearchContext);

   const hasCortexData = useMemo(() => {
        return rowData?.some(row => row.isFromCortex === true);
    }, [rowData]);
    const cortexColumn = {
        headerName: "Origem",
        field: "isFromCortex",
        flex: 1,
        cellRenderer: (params) => {
            const isFromCortex = params.data?.isFromCortex;

            const imgSrc = isFromCortex
                ? `${API_URL}/public/assets/img/cortex-enabled.svg`
                : `${API_URL}/public/assets/img/logo-sentry/sw.s.logo.svg`;

            return (
                <div className="flex justify-center align-center h-full">
                    <img
                        src={imgSrc}
                        width={24}
                        height={24}
                        style={{ display: "block", margin: "0 auto" }}
                        alt=""
                    />
                </div>
            );
        }
    }
    const hiddenIdColumn = {
        headerName: 'ID',
        field: 'id',
        filter: true,
        hide: true,
    };
    const pclColumn = {
        headerName: "PCL",
        field: "pcl",
        width: 60,
        cellClass: "py-1",
        sortable: false,
        cellRenderer: (params) => (
            <PclButton
                id={params.data?.id}
                color={resolveGridColor(
                    params.data,
                    colorRegistry,
                    options,
                    replicatedPlates
                )}
            />
        ),
    }
    const plateColumn =  {
        headerName: "Placa",
        field: "plate",
        width: 90,
        cellClass: "font-bold",
        valueFormatter: ({ value }) =>
            value?.formattedValue ??
            `${value?.substring(0, 3)}-${value?.substring(3, 7)}`,
    }
    const dtCaptureColumn = {
        headerName: "Data/hora",
        headerTooltip: "Data/hora",
        field: "dtCapture",
        flex: options.showTimeBetweenPassage ? 2 :  3,
        tooltipField: "dtCapture",
        valueFormatter: ({ data }) =>
            `(${format(
                parseISO(data.dtCaptureYmd),
                "eee",
                { locale: ptBR }
            ).substring(0, 3)}) ${data.dtCapture}`,
    }
    const timeBetweenPassageColumn = {
        headerName: "Tempo entre passagens",
        headerTooltip: "Tempo entre passagens",
        field: "timeBetweenPassage",
        flex: 3,
        sortable: false,
    }
    const qtdColumn = {
        headerName: "Quantidade",
        field: "qtd",
        flex: 2,
        sort: "desc",
        sortIndex: 0,
    }
    const traceColumn = {
        headerName: "",
        field: "trace",
        width: 60,
        cellClass: "py-1",
        cellRenderer: (params) =>
            params.data.qtd > 1 ? (
                <TraceRouteButton data={params.data.passages} />
            ) : null,
    }
    const routeColumn = {
        headerName: "Nome do ponto de coleta",
        headerTooltip: "Nome do ponto de coleta",
        field: "capturePlaceName",
        flex: options.showTimeBetweenPassage ? 3 :  4,
        tooltipField: "capturePlaceName",
    }

    const columnDefs = useMemo(() => {
        if (options.groupByPlate) {
            return [
                hiddenIdColumn,
                pclColumn,
                plateColumn,
                qtdColumn,
                traceColumn,
                ...(hasCortexData ? [cortexColumn] : [])
            ];
        }

        return [
            hiddenIdColumn,
            pclColumn,
            routeColumn,
            dtCaptureColumn,
            plateColumn,
            ...(options.showTimeBetweenPassage ? [timeBetweenPassageColumn] : []),
            ...(hasCortexData ? [cortexColumn] : [])
        ];
    }, [
        options.groupByPlate,
        options.showTimeBetweenPassage,
        colorRegistry,
        markersData,
        rowData,
        hasCortexData
    ]);

    function PclButton({ id, color }) {
        const glyphText = markersData.get(id)?.glyphText;

        return (
            <div
                className="flex items-center justify-center h-[30px] w-[30px] border rounded mx-auto"
                style={{ backgroundColor: color }}
            >
                <span className="font-bold">{glyphText}</span>
            </div>
        );
    }

    const onSortChanged = useCallback((params) => {
        const ordered = syncOrderedData(params.api);
        setOrderedData(ordered);
    }, []);

    const onFirstDataRendered = useCallback((params) => {
        const ordered = syncOrderedData(params.api);
        setOrderedData(ordered);
    }, []);


    function TraceRouteButton({ data : passages}) {
        return (
            <div className="flex justify-center items-center">
                <button className="h-[30px] w-[30px] bg-[#6c757d] flex justify-center items-center rounded cursor-pointer px-2 hover:bg-[#5a6268]" onClick={() => {
                    const plate = passages[0]?.plate?.rawValue;
                    if (!plate) return;

                    setActivePlates(prev => {
                        const next = new Set(prev);

                        if (next.has(plate)) {
                            next.delete(plate);
                        } else {
                            next.add(plate);
                        }

                        return next;
                    });
                }}
                ><FaRoute color="#fff" size={18}/></button>
            </div>
        );
    }

    const onRowClicked = (row) => {
        setCurrentPassage(0);
        centerOnSelectionRef.current = true;
        setSelection({
            type: 'single',
            data: row
        });
        setRegistryVersion(v => v + 1)
    };

    useEffect(() => {
        if (selection.type === 'none') return;
        if (!gridApi.current) return;

        if(selection.type === "multiple"){
            gridApi.current.deselectAll()
            return;
        }

        const rowNode = gridApi.current.getRowNode(selection.data.id);
        if (!rowNode) return;

        rowNode.setSelected(true);
        setHiddenDetails(false);
        gridApi.current.ensureIndexVisible(rowNode.rowIndex, 'middle');
    }, [selection]);

    useEffect(() => {
        if (!gridApi.current) return;

        if (options.orderAlphabetically) {
            gridApi.current.applyColumnState({
                state: [{ colId: 'dtCapture', sort: 'asc' }],
                defaultState: { sort: null }
            });
        } else {
            gridApi.current.applyColumnState({
                defaultState: { sort: null }
            });
        }
    }, [options.orderAlphabetically]);

    const activeIds = useMemo(() => {
        if (selection?.type !== 'multiple') return new Set();
        return new Set(selection.data.map(el => el.id));
    }, [selection]);


    return(
        <div className={[className, rowData.length === 50 ? "georeferenced-custom-table" : ""].join(" ")}>
            <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                animateRows={false}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                tooltipShowDelay={200}
                getRowStyle={params => {
                    if (!activeIds.size) return null;

                    if (activeIds.has(params.data.id)) {
                        return { backgroundColor: '#91b9eb' };
                    }
                }}
                suppressCellFocus={true}
                gridOptions={{getRowId: params => params.data.id, localeText: {...aggridUtils.localization(), noRowsToShow: "Sem informações para exibição."}}}
                theme={aggridUtils.generalTheme({rowHeight: 40})}
                onRowClicked={({data}) => onRowClicked(data)}
                onSortChanged={onSortChanged}
                rowSelection={{
                    mode: 'singleRow',
                    checkboxes: false,
                    headerCheckbox: false,
                    enableClickSelection: true
                }}
                overlayNoRowsTemplate={`
                    <div class="bg-[#ebebeb] w-full h-full flex items-center justify-center">
                       <span class="font-bold text-[#676a6c]">Sem informação para a exibição.</span>
                    </div>
                `}
            />
          { rowData.length === 50 &&
            <div className="custom-border text-center font-bold text-[16px]">
              A pesquisa atingiu o limite de 50 registros.
            </div>
          }
        </div>
    )
}

export default GeoreferencedSearchTable;